// @flow
import React, { useState } from 'react';
import type { Element } from 'react';
import { withTranslation } from '@i18n';

import styled from 'styled-components';
import { mt } from 'styled-components-spacing';

import { NextSeo } from 'next-seo';

import { FAQs, BannerLeft } from '@components';
import { YAPITA_HEALTH_URL } from '@routes';
import WhyUs from './components/WhyUs';
import HospitalNetworks from './components/HospitalNetworks';
import HowItWorks from './components/HowItWorks';
import Contact from './components/Contact';
import ContactEmployerDialog from './components/ContactEmployerDialog';

const StyledWrapper = styled.div`
  ${mt(5)}
`;

const ForHospitals = ({ t }): Element<any> => {
  const [dialogState, setDialogState] = useState<boolean>(false);
  const metaData: { MetaTitle: string, MetaDescription: string } = t('Seo:ForHospitals', {
    returnObjects: true
  });

  // to hide scroll on div under body when dialog is opened
  // since we cannot target parent element in styled component
  const setBodyOverflow = (value: ?string = ''): void => {
    const divUnderBody = document.getElementById('__next');

    if (divUnderBody) {
      if (value) {
        divUnderBody.style.top = `-${window.scrollY}px`;
        divUnderBody.style.position = value;
      } else {
        // when setting position fixed for div under body, it resets the page to the top
        // to take the page where user leaves it when the user opens the dialog
        const scrollY = divUnderBody.style.top;
        divUnderBody.style.position = '';
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
  };

  const showDialog = (): void => {
    setBodyOverflow('fixed');
    setDialogState(true);
  };

  const hideDialog = (): void => {
    setBodyOverflow();
    setDialogState(false);
  };

  return (
    <>
      <NextSeo
        title={metaData.MetaTitle}
        description={metaData.MetaDescription}
        canonical={`${YAPITA_HEALTH_URL}/en/for-hospitals`}
        openGraph={{
          url: `${YAPITA_HEALTH_URL}/en/for-hospitals`,
          title: metaData.MetaTitle,
          description: metaData.MetaDescription,
          images: [
            {
              url: 'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />
      <StyledWrapper>
        <WhyUs />

        <BannerLeft />

        <HospitalNetworks showDialog={showDialog} />

        <HowItWorks />

        <Contact showDialog={showDialog} />

        <ContactEmployerDialog isDialogOpen={dialogState} hideDialog={hideDialog} />

        <FAQs kind="hospitals" imageSrc="../../../static/images/question.svg" />
      </StyledWrapper>
    </>
  );
};

ForHospitals.getInitialProps = async () => ({
  namespacesRequired: ['Hospitals']
});

export default withTranslation('Hospitals')(ForHospitals);
